import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Label } from '../UI/Label';
import { Alert, AlertDescription } from '../UI/Alert';
import {
  ArrowPathIcon as Loader2,
  ShieldCheckIcon as Shield,
  DocumentDuplicateIcon as Copy,
  CheckIcon as Check,
  ExclamationTriangleIcon as AlertTriangle,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { BackupCodes } from './BackupCodes';

interface TwoFactorSetupProps {
  onComplete?: (backupCodes?: string[]) => void;
  onCancel?: () => void;
}

interface SetupResponse {
  qrcode_png_base64: string;
  otpauth_uri: string;
  backup_codes: string[];
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<'initial' | 'qr' | 'verify' | 'backup'>('initial');
  const [setupData, setSetupData] = useState<SetupResponse | null>(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedCodes, setCopiedCodes] = useState<Set<number>>(new Set());
  const { user } = useAuth();

  const handleStartSetup = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/v2/auth/2fa/setup');
      setSetupData(response.data);
      setStep('qr');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao configurar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Digite um código de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/api/v2/auth/2fa/confirm', { otp });
      setStep('backup');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async (code: string, index: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCodes(prev => new Set([...Array.from(prev), index]));
      setTimeout(() => {
        setCopiedCodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Erro ao copiar código:', err);
    }
  };

  const handleComplete = () => {
    onComplete?.(setupData?.backup_codes);
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar a configuração do 2FA?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Cancel the 2FA setup process
      await api.delete('/api/v2/auth/2fa/setup');
      onCancel?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao cancelar configuração');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'initial') {
    return (
      <Card className='w-full max-w-md mx-auto'>
        <CardHeader className='text-center'>
          <div className='mx-auto mb-4 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center'>
            <Shield className='w-6 h-6 text-blue-600' />
          </div>
          <CardTitle>Configurar Autenticação de Dois Fatores</CardTitle>
          <CardDescription>Adicione uma camada extra de segurança à sua conta</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='text-sm text-gray-600'>
            <p className='mb-2'>A autenticação de dois fatores (2FA) protege sua conta exigindo:</p>
            <ul className='list-disc list-inside space-y-1 ml-4'>
              <li>Sua senha</li>
              <li>Um código do seu aplicativo autenticador</li>
            </ul>
          </div>

          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className='flex gap-2'>
            <Button onClick={handleStartSetup} disabled={loading} className='flex-1'>
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Configurar 2FA
            </Button>
            <Button variant='outline' onClick={onCancel} className='flex-1'>
              Cancelar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'qr') {
    return (
      <Card className='w-full max-w-md mx-auto'>
        <CardHeader className='text-center'>
          <CardTitle>Escaneie o Código QR</CardTitle>
          <CardDescription>Use seu aplicativo autenticador para escanear o código</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='text-center'>
            {setupData && (
              <img
                src={`data:image/png;base64,${setupData.qrcode_png_base64}`}
                alt='QR Code para 2FA'
                className='mx-auto border rounded-lg'
              />
            )}
          </div>

          <div className='text-sm text-gray-600'>
            <p className='mb-2'>Aplicativos recomendados:</p>
            <ul className='list-disc list-inside space-y-1 ml-4'>
              <li>Google Authenticator</li>
              <li>Authy</li>
              <li>Microsoft Authenticator</li>
            </ul>
          </div>

          <Button onClick={() => setStep('verify')} className='w-full'>
            Continuar
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'verify') {
    return (
      <Card className='w-full max-w-md mx-auto'>
        <CardHeader className='text-center'>
          <CardTitle>Verificar Configuração</CardTitle>
          <CardDescription>Digite o código de 6 dígitos do seu aplicativo</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='otp'>Código de Verificação</Label>
            <Input
              id='otp'
              type='text'
              placeholder='000000'
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className='text-center text-lg tracking-widest'
              maxLength={6}
            />
          </div>

          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className='flex gap-2'>
            <Button onClick={() => setStep('qr')} variant='outline' className='flex-1'>
              Voltar
            </Button>
            <Button onClick={handleVerifyOtp} disabled={loading || otp.length !== 6} className='flex-1'>
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Verificar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'backup') {
    return (
      <Card className='w-full max-w-md mx-auto'>
        <CardHeader className='text-center'>
          <CardTitle>Códigos de Backup</CardTitle>
          <CardDescription>
            Salve estes códigos em um local seguro. Você pode usá-los se perder acesso ao seu aplicativo autenticador.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Alert>
            <AlertDescription>
              <strong>Importante:</strong> Cada código só pode ser usado uma vez. Guarde-os em um local seguro!
            </AlertDescription>
          </Alert>

          <BackupCodes codes={setupData?.backup_codes || []} onClose={handleComplete} />
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default TwoFactorSetup;
